const ISSUER = (import.meta.env.VITE_KEYCLOAK_STAFF_ISSUER as string | undefined)?.replace(/\/$/, '');
const CLIENT_ID = (import.meta.env.VITE_KEYCLOAK_STAFF_CLIENT_ID as string | undefined) ?? 'servicesai-console';
const REDIRECT_URI =
  (import.meta.env.VITE_KEYCLOAK_STAFF_REDIRECT_URI as string | undefined) ??
  (typeof window === 'undefined' ? '' : `${window.location.origin}/`);
/** Keycloak identity-provider alias to jump straight to (the shared ministry
 *  realm brokers Microsoft Entra under the alias `microsoft`, same as
 *  moei.agentic.ae). Set to an empty string to show the Keycloak form. */
const IDP_HINT = (import.meta.env.VITE_KEYCLOAK_STAFF_IDP_HINT as string | undefined) ?? 'microsoft';

const SESSION_KEY = 'sai.staffOidc';
const PKCE_KEY = 'sai.staffOidc.pkce';

interface StoredSession {
  accessToken: string;
  refreshToken?: string;
  /** Kept for Keycloak end-session (id_token_hint) so logout is seamless. */
  idToken?: string;
  expiresAt: number;
}

function encode(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

function randomValue(bytes = 32): string {
  return encode(crypto.getRandomValues(new Uint8Array(bytes)));
}

async function challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return encode(new Uint8Array(digest));
}

function readSession(): StoredSession | null {
  try {
    const value = localStorage.getItem(SESSION_KEY);
    return value ? JSON.parse(value) as StoredSession : null;
  } catch {
    return null;
  }
}

export function staffSsoConfigured(): boolean {
  return Boolean(ISSUER && CLIENT_ID && REDIRECT_URI);
}

export function staffSsoCredential(): string | null {
  const session = readSession();
  return session && session.expiresAt > Date.now() + 15_000 ? `Bearer ${session.accessToken}` : null;
}

export async function startStaffSso(): Promise<void> {
  if (!ISSUER) throw new Error('Staff SSO issuer is not configured');
  const verifier = randomValue(48);
  const state = randomValue(24);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }));
  const url = new URL(`${ISSUER}/protocol/openid-connect/auth`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('redirect_uri', REDIRECT_URI);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid profile email');
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', await challenge(verifier));
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('prompt', 'login');
  // Skip Keycloak's own login form and go directly to the Microsoft sign-in.
  if (IDP_HINT) url.searchParams.set('kc_idp_hint', IDP_HINT);
  window.location.assign(url);
}

export async function completeStaffSsoCallback(): Promise<boolean> {
  if (!ISSUER) return false;
  const callback = new URL(window.location.href);
  const code = callback.searchParams.get('code');
  const state = callback.searchParams.get('state');
  if (!code) return false;
  const saved = sessionStorage.getItem(PKCE_KEY);
  sessionStorage.removeItem(PKCE_KEY);
  if (!saved) throw new Error('SSO callback is missing its PKCE session');
  const pkce = JSON.parse(saved) as { verifier: string; state: string };
  if (!state || state !== pkce.state) throw new Error('SSO callback state did not match');
  const response = await fetch(`${ISSUER}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      code,
      code_verifier: pkce.verifier,
    }),
  });
  if (!response.ok) throw new Error('Keycloak token exchange failed');
  const tokens = await response.json() as {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    expires_in: number;
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify({
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    idToken: tokens.id_token,
    expiresAt: Date.now() + tokens.expires_in * 1_000,
  } satisfies StoredSession));
  callback.searchParams.delete('code');
  callback.searchParams.delete('state');
  callback.searchParams.delete('session_state');
  callback.searchParams.delete('iss');
  window.history.replaceState({}, '', `${callback.pathname}${callback.search}${callback.hash}`);
  return true;
}

export async function refreshStaffSso(): Promise<boolean> {
  if (!ISSUER) return false;
  const session = readSession();
  if (!session?.refreshToken) return false;
  if (session.expiresAt > Date.now() + 60_000) return true;
  const response = await fetch(`${ISSUER}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: CLIENT_ID,
      refresh_token: session.refreshToken,
    }),
  });
  if (!response.ok) {
    localStorage.removeItem(SESSION_KEY);
    return false;
  }
  const tokens = await response.json() as {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    expires_in: number;
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify({
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token ?? session.refreshToken,
    idToken: tokens.id_token ?? session.idToken,
    expiresAt: Date.now() + tokens.expires_in * 1_000,
  } satisfies StoredSession));
  return true;
}

export function clearStaffSso(): void {
  localStorage.removeItem(SESSION_KEY);
}

/**
 * Full console logout: clears the local session AND ends the Keycloak realm
 * session (front-channel end_session with id_token_hint, so no confirmation
 * screen), landing back on the console sign-in gate. Without SSO configured it
 * only clears local state.
 */
export function logoutStaffSso(): void {
  const session = readSession();
  clearStaffSso();
  if (!ISSUER) return;
  const url = new URL(`${ISSUER}/protocol/openid-connect/logout`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('post_logout_redirect_uri', `${window.location.origin}/`);
  if (session?.idToken) url.searchParams.set('id_token_hint', session.idToken);
  window.location.assign(url);
}
